import React, { memo, useState, useMemo, useCallback, useEffect } from 'react'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useTheme } from '@material-ui/core/styles'
import Button from '@material-ui/core/Button'
import { useTranslation } from 'react-i18next'
import FormControl from '@material-ui/core/FormControl'
import InputLabel from '@material-ui/core/InputLabel'
import Input from '@material-ui/core/Input'
import { createStyles, Theme, makeStyles } from '@material-ui/core/styles'
import Grid from '@material-ui/core/Grid'
import Checkbox from '@material-ui/core/Checkbox'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Datetime from 'react-datetime'
import { Select, MenuItem } from '@material-ui/core'

import { Loading } from 'components'
import {
	useDeleteFromExperienceCollectionMutation,
	useRoLocationsCollectionQuery,
	useSetExperienceMutation,
} from '../../../../../graphql/__generated__/graphql'

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		container: {
			display: 'flex',
			flexWrap: 'wrap',
		},
		formControl: {
			margin: theme.spacing(1),
			width: '97%',
		},
		overflowYVisible: {
			overflowY: 'visible',
			padding: '8px 8px',
		},
		dialogTitle: {
			paddingLeft: '8px',
			paddingRight: '8px',
		},
	}),
)
interface ExperienceModalProps {
	open: boolean
	toggle: () => void
	data: any
	updateUser: Function
}

export const ExperienceModal: React.FunctionComponent<ExperienceModalProps> =
	memo(({ open, toggle, data, updateUser }) => {
		const theme = useTheme()
		const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
		const { t } = useTranslation()
		const classes = useStyles()
		const { data: regions } = useRoLocationsCollectionQuery({
			variables: {
				data: {
					type: {
						in: ['Judeţe', 'National capital'],
					},
				},
			},
		})
		const [title, setTitle] = useState<string | ''>(data ? data.title : '')
		const [description, setDescription] = useState<string | ''>(
			data ? data.description : '',
		)
		const [region, setRegion] = useState<string | undefined>(
			data && data.location ? data.location.region2 : undefined,
		)
		// const [location, setLocation] = useState<string | undefined>(
		// 	data && data.location ? data.location.name : undefined,
		// )
		const [locationId, setLocationId] = useState<string | undefined>(
			data && data.location ? data.location.locationId : '',
		)
		const [position, setPosition] = useState<string>(
			data && data.position ? data.position : '',
		)
		const [company, setCompany] = useState<string>(
			data && data.company ? data.company : '',
		)
		const [isPresent, setIsPresent] = useState<boolean>(
			data && data.isPresent ? data.isPresent : false,
		)
		const [isCurrent, setIsCurrent] = useState<boolean>(
			data && data.isCurrent ? data.isCurrent : false,
		)
		const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false)
		const { data: locations } = useRoLocationsCollectionQuery({
			skip: !region,
			variables: {
				data: {
					not: {
						type: {
							in: ['Judeţe', 'National capital'],
						},
					},

					region2: { eq: region },
				},
			},
		})
		const handleTitleChange = (
			event: React.ChangeEvent<HTMLInputElement>,
		) => {
			setTitle(event.target.value)
		}
		const handleDescriptionChange = (
			event: React.ChangeEvent<HTMLInputElement>,
		) => {
			setDescription(event.target.value)
		}
		const handleLocationChange = (event: any) => {
			setLocationId(event.target.value)
		}
		const handleRegionChange = (event: any) => {
			setRegion(event.target.value)
		}
		const handlePositionChange = (
			event: React.ChangeEvent<HTMLInputElement>,
		) => {
			setPosition(event.target.value)
		}
		const handleCompanyChange = (
			event: React.ChangeEvent<HTMLInputElement>,
		) => {
			setCompany(event.target.value)
		}
		const handleIsPresentChange = (
			event: React.ChangeEvent<HTMLInputElement>,
		) => {
			setIsPresent(event.target.checked)
			event.target.checked
				? setEndDatetime(undefined)
				: setEndDatetime(startDatetime.add(5, 'minutes'))
		}
		const handleIsCurrentChange = (
			event: React.ChangeEvent<HTMLInputElement>,
		) => {
			setIsCurrent(event.target.checked)
		}
		const currentTime = useMemo(
			() =>
				Datetime.moment().add(
					5 - (Datetime.moment().minute() % 5),
					'minutes',
				),
			[],
		)
		const [startDatetime, setStartDatetime] = useState(
			(data && data.startDate) || currentTime,
		)
		const [endDatetime, setEndDatetime] = useState(
			(data && data.endDate) || currentTime,
		)
		const cleanState = useCallback(() => {
			setTitle('')
			setDescription('')
			setRegion('')
			setPosition('')
			setCompany('')
			setIsPresent(false)
			setIsCurrent(false)
			setEndDatetime('')
			setStartDatetime('')
		}, [])

		const [updateMutationHandle, { loading: updateMutationLoading }] =
			useSetExperienceMutation({
				update(cache, { data }) {
					try {
						// const cachedExperience = cache.readQuery({
						// 	query: UserProfileQuery,
						// 	variables: { uid: '' },
						// })
					} catch (error) {
						console.error(error)
					}
				},
			})
		const [deleteMutationHandle, { loading: loadingDeleteMutationHandle }] =
			useDeleteFromExperienceCollectionMutation()

		const save = useCallback(async () => {
			await updateMutationHandle({
				variables: {
					id: data ? data.id : '',
					title,
					description,
					locationId,
					position,
					company,
					startDate: startDatetime,
					endDate: !isPresent ? endDatetime : null,
					isPresent,
					isCurrent,
				},
			})
			updateUser()
			toggle()
			cleanState()
		}, [
			title,
			description,
			position,
			company,
			startDatetime,
			endDatetime,
			isPresent,
			isCurrent,
			toggle,
			updateMutationHandle,
			locationId,
			updateUser,
			data,
			cleanState,
		])

		const updateEndDatetime = useCallback(
			dt => {
				if (dt && dt.isAfter(startDatetime)) {
					setEndDatetime(dt)
				} else {
					setEndDatetime(startDatetime.add(5, 'minutes'))
				}
			},
			[setEndDatetime, startDatetime],
		)
		const updateStartDatetime = useCallback(
			dt => {
				setStartDatetime(dt)
				if (endDatetime && !endDatetime.isAfter(dt))
					setEndDatetime(dt.add(5, 'minutes'))
			},
			[setStartDatetime, endDatetime, setEndDatetime],
		)

		const toggleDeleteModal = useCallback(() => {
			setOpenDeleteModal(openDeleteModal => !openDeleteModal)
		}, [])

		const deleteExperience = useCallback(async () => {
			await deleteMutationHandle({
				variables: {
					id: data ? data.id : '',
				},
			})
			updateUser()
			toggleDeleteModal()
			toggle()
			cleanState()
		}, [
			deleteMutationHandle,
			toggle,
			updateUser,
			toggleDeleteModal,
			data,
			cleanState,
		])

		useEffect(() => {
			setTitle(data && data.title ? data.title : '')
			setDescription(data && data.description ? data.description : '')
			setStartDatetime(
				Datetime.moment(
					data && data.startDate ? data.startDate : currentTime,
				),
			)
			setEndDatetime(
				Datetime.moment(
					data && data.endDate ? data.endDate : currentTime,
				),
			)
			setIsPresent(data && data.isPresent ? data.isPresent : false)
			setIsCurrent(data && data.isCurrent ? data.isCurrent : false)
			setRegion(
				data && data.location && data.location.region2
					? data.location.region2
					: '',
			)
			setLocationId(
				data && data.location && data.location.id
					? data.location.id
					: '',
			)
			setCompany(data && data.company ? data.company : '')
			setPosition(data && data.position ? data.position : '')
		}, [data, currentTime])

		const handleCancelButton = useCallback(() => {
			toggle() // hide the dialog
			cleanState()
		}, [toggle, cleanState])

		return (
			<>
				<Dialog
					fullScreen={fullScreen}
					open={open}
					aria-labelledby="responsive-dialog-title"
					classes={{
						paperScrollPaper: classes.overflowYVisible,
					}}
				>
					<DialogTitle
						id="responsive-dialog-title"
						classes={{ root: classes.dialogTitle }}
					>
						{data && data.id
							? t('Edit experience')
							: t('Add new experience')}
					</DialogTitle>
					<DialogContent classes={{ root: classes.overflowYVisible }}>
						<Grid container spacing={0}>
							<Grid item xs={12}>
								<FormControl className={classes.formControl}>
									<InputLabel htmlFor="experience_title">
										{t('Title')}
									</InputLabel>
									<Input
										id="experience_title"
										value={title}
										onChange={handleTitleChange}
									/>
								</FormControl>
							</Grid>
							<Grid item xs={12}>
								<FormControl className={classes.formControl}>
									<InputLabel htmlFor="experience_description">
										{t('Description')}
									</InputLabel>
									<Input
										id="experience_description"
										value={description}
										onChange={handleDescriptionChange}
										multiline={true}
										rows={2}
										rowsMax={4}
									/>
								</FormControl>
							</Grid>
							<Grid item xs={6} sm={4}>
								<Datetime
									timeFormat={false}
									// isValidDate={(current: any) =>
									// 	current.isAfter(Datetime.moment())
									// }
									value={startDatetime}
									closeOnSelect={true}
									onChange={updateStartDatetime}
									inputProps={{
										placeholder: t('Start'),
										readOnly: true,
									}}
									renderInput={(
										props: any,
										open: any,
										close: any,
									) => {
										return (
											<FormControl
												style={{
													maxWidth: '70%',
													marginRight:
														theme.spacing(2),
													marginLeft:
														theme.spacing(1),
												}}
											>
												<InputLabel htmlFor="experience_startDate">
													{t('Start')}
												</InputLabel>
												<Input {...props} />
											</FormControl>
										)
									}}
								/>
							</Grid>
							<Grid item xs={6} sm={4}>
								<Datetime
									open={isPresent ? false : undefined}
									timeFormat={false}
									isValidDate={(current: any) =>
										current.isAfter(startDatetime)
									}
									value={!isPresent ? endDatetime : undefined}
									closeOnSelect={true}
									onChange={updateEndDatetime}
									inputProps={{
										placeholder: t('End'),
										readOnly: true,
										disabled: isPresent,
									}}
									renderInput={(
										props: any,
										open: any,
										close: any,
									) => {
										return (
											<FormControl
												style={{
													maxWidth: '70%',
													marginRight:
														theme.spacing(2),
												}}
											>
												<InputLabel
													disabled={isPresent}
													htmlFor="experience_endDate"
												>
													{t('End')}
												</InputLabel>
												<Input
													disabled={isPresent}
													{...props}
												/>
											</FormControl>
										)
									}}
								/>
							</Grid>
							<Grid item xs={12} sm={4}>
								<FormControlLabel
									style={{
										paddingTop: theme.spacing(1),
										marginLeft: fullScreen
											? 0
											: theme.spacing(2),
									}}
									control={
										<Checkbox
											value={isPresent}
											checked={isPresent}
											onChange={handleIsPresentChange}
											color="primary"
										/>
									}
									label={t('Present')}
								/>
							</Grid>
							<Grid
								item
								xs={12}
								style={{ display: 'inline-flex' }}
							>
								<FormControl className={classes.formControl}>
									<InputLabel htmlFor="experience-region">
										{t('Region')}
									</InputLabel>
									<Select
										value={region ? region : ''}
										onChange={handleRegionChange}
										inputProps={{
											name: t('Region'),
											id: 'experience-region',
										}}
									>
										{regions &&
											regions?.roLocationsCollection &&
											regions?.roLocationsCollection?.edges
												?.map(x => x.node)
												?.map(item => {
													return (
														<MenuItem
															key={item.id}
															value={item.name}
														>
															{item.name}
														</MenuItem>
													)
												})}
									</Select>
								</FormControl>
								<FormControl className={classes.formControl}>
									<InputLabel htmlFor="experience-location">
										{t('Location')}
									</InputLabel>
									<Select
										value={locationId ? locationId : ''}
										disabled={!region}
										onChange={handleLocationChange}
										inputProps={{
											name: t('Location'),
											id: 'experience-location',
										}}
									>
										{locations &&
											locations.roLocationsCollection
												?.edges &&
											locations.roLocationsCollection.edges
												?.map(x => x.node)
												?.map((item: any) => {
													return (
														<MenuItem
															key={item.id}
															value={item.id}
														>
															{item.name}
														</MenuItem>
													)
												})}
									</Select>
								</FormControl>
							</Grid>
							<Grid
								item
								xs={12}
								style={{ display: 'inline-flex' }}
							>
								<FormControl className={classes.formControl}>
									<InputLabel htmlFor="experience_company">
										{t('Company')}
									</InputLabel>
									<Input
										id="experience_company"
										value={company}
										onChange={handleCompanyChange}
										multiline={true}
										rows={1}
										rowsMax={1}
									/>
								</FormControl>
								<FormControl className={classes.formControl}>
									<InputLabel htmlFor="experience_position">
										{t('Position')}
									</InputLabel>
									<Input
										id="experience_position"
										value={position}
										onChange={handlePositionChange}
										multiline={true}
										rows={1}
										rowsMax={1}
									/>
								</FormControl>
							</Grid>
							<Grid item>
								<FormControlLabel
									style={{
										paddingTop: theme.spacing(1),
										marginLeft: theme.spacing(-0.5),
									}}
									control={
										<Checkbox
											value={isCurrent}
											checked={isCurrent}
											onChange={handleIsCurrentChange}
											color="primary"
										/>
									}
									label={t('Use this as my current position')}
								/>
							</Grid>
						</Grid>
					</DialogContent>
					<DialogActions>
						{updateMutationLoading ? (
							<Loading size={32} style={{ marginRight: 5 }} />
						) : (
							<>
								{data && data.id && (
									<Button
										onClick={toggleDeleteModal}
										style={{
											background: '#fff',
											alignSelf: 'flex-start',
											marginLeft: 20,
											marginRight: 'auto',
										}}
										color={'secondary'}
									>
										{t('Delete')}
									</Button>
								)}
								<Button
									onClick={handleCancelButton}
									color="primary"
								>
									{t('Cancel')}
								</Button>
								<Button
									onClick={save}
									color="primary"
									autoFocus
								>
									{t('Save')}
								</Button>
							</>
						)}
					</DialogActions>
				</Dialog>
				<Dialog
					open={openDeleteModal}
					// onClose={toggleDeleteModal}
					aria-labelledby="responsive-dialog-title"
				>
					<DialogTitle id="responsive-dialog-title">
						{t('Are you sure you want to delete this experience?')}
					</DialogTitle>
					<DialogActions>
						{loadingDeleteMutationHandle ? (
							<Loading size={32} style={{ marginRight: 5 }} />
						) : (
							<>
								<Button
									color="primary"
									onClick={deleteExperience}
								>
									{t('Yes')}
								</Button>
								<Button
									color="primary"
									autoFocus
									onClick={toggleDeleteModal}
								>
									{t('No')}
								</Button>
							</>
						)}
					</DialogActions>
				</Dialog>
			</>
		)
	})
